// 멤버십 표시용 포맷 유틸 — 크레딧은 원(KRW) 정수, 공급가 기준(VAT 별도)

import { MembershipTier, MembershipStatus, OverageBillStatus, BillType, MembershipBill, WorkLogRelatedType } from '@/lib/types/membership';
import { MEMBERSHIP_PLANS } from '@/lib/constants/membership-plans';

// 원 단위 금액 → "330,000원"
export const formatWon = (amount: number): string => `${amount.toLocaleString('ko-KR')}원`;

// 부호를 살려서 표시 (원장 행용) → "+500,000원" / "-200,000원"
export const formatSignedWon = (amount: number): string => `${amount > 0 ? '+' : ''}${amount.toLocaleString('ko-KR')}원`;

export const TIER_LABEL: Record<MembershipTier, string> = {
  BRONZE: '브론즈',
  SILVER: '실버',
  GOLD: '골드',
};

/**
 * 등급별 금액. **`lib/constants/membership-plans` 하나에서 나온다.**
 *
 * 예전에는 여기 숫자를 따로 적어 뒀고, 실제로 갈렸다 — 요금 안내와 마이페이지가 크레딧
 * 60/90/130만원을 약속하는 동안 이 표는 50/80/120만원을 들고 있었고, 어드민 부여 화면이
 * 그 값을 "등급 기본값"이라고 안내했다(2026-09-16 발견). 등급 금액을 바꿀 일이 생기면
 * 상수 파일만 고친다.
 */
const byTier = <T>(pick: (plan: (typeof MEMBERSHIP_PLANS)[number]) => T): Record<MembershipTier, T> =>
  Object.fromEntries(MEMBERSHIP_PLANS.map(plan => [plan.tier, pick(plan)])) as Record<MembershipTier, T>;

/** 등급별 월 구독료 공급가(원, VAT 별도). VAT 포함액은 ×1.1. */
export const TIER_MONTHLY_SUPPLY: Record<MembershipTier, number> = byTier(plan => plan.monthlyPrice);

/**
 * 등급별 월 크레딧 금액(원, 공급가). 구독료보다 큰 만큼이 상위 플랜 혜택이다.
 *
 * **이건 "서버 기본값"이 아니다.** 서버에도 등급 기본값이 있지만(`tierDefaultCreditAmount`)
 * 그쪽은 50/80/120만원이고 고칠 API 가 없다. 그래서 부여·수정 요청에 이 금액을 **명시해서**
 * 보낸다 — 크레딧 칸을 비워도 서버 기본값이 적용되지 않게(`MembershipCreateModal`).
 * 서버 기본값이 이 표와 같아지면 그때 명시 전송을 걷어내도 된다.
 */
export const TIER_CREDIT_AMOUNT: Record<MembershipTier, number> = byTier(plan => plan.monthlyCredit);

// 등급 기본 구독료 VAT 포함액(원). 어드민 청구 기본값·표시용. 최종액은 서버 산정.
export const tierMonthlyFeeTotal = (tier: MembershipTier): number => Math.round(TIER_MONTHLY_SUPPLY[tier] * 1.1);

export const MEMBERSHIP_STATUS_LABEL: Record<MembershipStatus, string> = {
  ACTIVE: '이용중',
  SUSPENDED: '일시중지',
  ENDED: '종료',
};

export const BILL_STATUS_LABEL: Record<OverageBillStatus, string> = {
  PENDING: '결제대기',
  PAID: '결제완료',
  CANCELLED: '취소',
};

export const BILL_TYPE_LABEL: Record<BillType, string> = {
  OVERAGE: '초과분',
  FEE: '구독료',
};

// 케이스 표시 라벨 — 백엔드는 제목/상태를 내려주지 않으므로 종류+번호로 표기한다.
// 백엔드 MembershipBillingService.caseLabel() 과 같은 규칙.
export const CASE_TYPE_LABEL: Record<WorkLogRelatedType, string> = {
  QUICK_INTAKE: '빠른 접수',
  CONTRACT: '계약',
  MANUAL: '기타',
};

export const caseLabel = (relatedType: WorkLogRelatedType, relatedId: number | null): string => (relatedId === null ? CASE_TYPE_LABEL.MANUAL : `${CASE_TYPE_LABEL[relatedType]} #${relatedId}`);

// 작업기록 최소 금액(원) — 백엔드 MembershipCreditCalculator.MIN_WORK_AMOUNT 와 동일.
export const MIN_WORK_AMOUNT = 1000;

// 작업 금액 검증 — 양의 정수, 최소 1,000원. 서버도 재검증한다.
export const isValidWorkAmount = (amount: number): boolean => Number.isInteger(amount) && amount >= MIN_WORK_AMOUNT;

// 월 크레딧 금액 검증 — 0원 이상 정수(크레딧 없는 멤버십도 허용). 서버 규칙과 동일.
export const isValidCreditAmount = (amount: number): boolean => Number.isInteger(amount) && amount >= 0;

// 수동 보정 금액 검증 — 0이 아닌 정수(±). 백엔드 MembershipCreditCalculator.validateAdjustAmount 와 동일.
export const isValidAdjustAmount = (amount: number): boolean => Number.isInteger(amount) && amount !== 0;

/**
 * 차감 미리보기 — 잔여 대비 입력 금액이 어떻게 쪼개지는지.
 * 백엔드 MembershipCreditCalculator.split() 과 같은 규칙(잔여 내 차감 + 초과 누적).
 * 예: 잔여 500,000 / 입력 600,000 → { deduct: 500,000, overage: 100,000 }
 */
export const previewDeduction = (remainingAmount: number, amount: number): { deduct: number; overage: number } => {
  const safeRemaining = Math.max(0, remainingAmount);
  const safeAmount = Math.max(0, amount);
  const deduct = Math.min(safeRemaining, safeAmount);
  return { deduct, overage: safeAmount - deduct };
};

// 초과분 VAT 포함 청구액 — 서버 산정과 동일(공급가 × 1.1 반올림). 표시용 추정치.
export const withVat = (supplyAmount: number): number => Math.round(supplyAmount * 1.1);

// 멤버십 결제 진입 URL (Toss 위젯, orderId = payment_uuid). 초과/구독료 공통.
export const buildMembershipPaymentUrl = (bill: MembershipBill): string => {
  const isFee = bill.billType === 'FEE';
  const params = new URLSearchParams({
    type: isFee ? 'MEMBERSHIP_FEE' : 'MEMBERSHIP_OVERAGE',
    orderId: bill.paymentUuid ?? '',
    totalPrice: String(bill.totalAmount),
    contractDocumentName: isFee ? '멤버십 구독료' : '멤버십 초과분',
    work: '',
  });
  return `/toss-payments?${params.toString()}`;
};

// 데스크톱은 결제 팝업, 모바일은 라우터 이동(기존 결제 흐름과 동일 패턴).
const PAYMENT_POPUP_FEATURES = 'width=650,height=650,menubar=no,toolbar=no,scrollbars=no,status=no';

export const openMembershipPayment = (bill: MembershipBill, mobile: boolean, push: (url: string) => void): void => {
  const url = buildMembershipPaymentUrl(bill);
  if (mobile) {
    push(url);
    return;
  }
  window.open(url, 'paymentPopup', PAYMENT_POPUP_FEATURES);
};
